import type { ReactNode, RefObject } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TimeSeriesTableHelpDemoTable } from './TimeSeriesTableHelpDemoTable'
import { TimeSeriesTableHelpTabId } from './timeSeriesTableHelp.helper'
import {
  getDemoFrameDurationMs,
  getDemoStateAtFrame,
  getFinalDemoState,
  TimeSeriesTableHelpDemoAnchor,
  TimeSeriesTableHelpDemoState,
} from './timeSeriesTableHelpDemo.helper'

type TimeSeriesTableHelpDemoProps = {
  tabId: TimeSeriesTableHelpTabId
  hint: string
  hasSelectionCheckboxes: boolean
}

type PointerPosition = { x: number; y: number }

type DemoProgress = { tabId: TimeSeriesTableHelpTabId; frame: number }

export function TimeSeriesTableHelpDemo({
  tabId,
  hint,
  hasSelectionCheckboxes,
}: TimeSeriesTableHelpDemoProps): ReactNode {
  const { t } = useTranslation()
  const state = useTimeSeriesTableHelpDemo(tabId)
  const panelRef = useRef<HTMLDivElement>(null)
  const pointerPosition = usePointerPosition(panelRef, state.pointer)

  return (
    <div
      ref={panelRef}
      aria-hidden="true"
      className="relative w-[316px] shrink-0 rounded-[5px] border border-grey-3 bg-grey-1 p-[14px]"
    >
      <span className="mb-[10px] block text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-grey-6">
        {t('Demo playing')}
      </span>

      <TimeSeriesTableHelpDemoTable tabId={tabId} state={state} hasSelectionCheckboxes={hasSelectionCheckboxes} />

      <p className="pt-[10px] text-[0.6875rem] leading-[1.5] text-grey-6">{hint}</p>

      {pointerPosition && (
        <span
          className="pointer-events-none absolute left-0 top-0 z-20 transition-transform duration-300 ease-out"
          style={{ transform: `translate(${pointerPosition.x}px, ${pointerPosition.y}px)` }}
        >
          {state.pressed && (
            <span className="absolute -left-[7px] -top-[7px] block h-[18px] w-[18px] rounded-full bg-primary-4/30" />
          )}
          <i className="fas fa-mouse-pointer text-[13px] text-primary-7" />
        </span>
      )}
    </div>
  )
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Advances the tab's script one frame at a time and derives what to show from the frame number,
 * so switching tabs restarts the script without the caller having to remount the panel.
 */
function useTimeSeriesTableHelpDemo(tabId: TimeSeriesTableHelpTabId): TimeSeriesTableHelpDemoState {
  const [reducedMotion] = useState(prefersReducedMotion)
  const [progress, setProgress] = useState<DemoProgress>({ tabId, frame: 0 })

  const frame = progress.tabId === tabId ? progress.frame : 0

  useEffect(() => {
    if (reducedMotion) return

    const timer = setTimeout(() => setProgress({ tabId, frame: frame + 1 }), getDemoFrameDurationMs(tabId, frame))
    return () => clearTimeout(timer)
  }, [reducedMotion, tabId, frame])

  return reducedMotion ? getFinalDemoState(tabId) : getDemoStateAtFrame(tabId, frame)
}

function usePointerPosition(
  panelRef: RefObject<HTMLDivElement | null>,
  anchor: TimeSeriesTableHelpDemoAnchor | null
): PointerPosition | null {
  const [position, setPosition] = useState<PointerPosition | null>(null)

  useLayoutEffect(() => {
    const panel = panelRef.current
    const target = anchor ? panel?.querySelector(`[data-demo-anchor="${anchor}"]`) : null
    if (!panel || !target) {
      setPosition(null)
      return
    }

    const panelBounds = panel.getBoundingClientRect()
    const targetBounds = target.getBoundingClientRect()
    setPosition({
      x: targetBounds.left - panelBounds.left + targetBounds.width / 2,
      y: targetBounds.top - panelBounds.top + targetBounds.height / 2,
    })
  }, [panelRef, anchor])

  return position
}
